/**
 * 🎨 CID Logos Management Component
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Upload, 
  Trash2, 
  Eye, 
  Copy, 
  Image, 
  Plus,
  AlertCircle,
  X
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const CidLogos = () => {
  const [showUpload, setShowUpload] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const queryClient = useQueryClient();

  // Load CID logos
  const { data: cidLogos, isLoading } = useQuery('cidLogos', async () => {
    const response = await axios.get('/api/cid-logos');
    return response.data.data;
  });

  // Delete logo mutation
  const deleteLogoMutation = useMutation(
    async (logoId) => {
      await axios.delete(`/api/cid-logos/${logoId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cidLogos');
        toast.success('CID-Logo erfolgreich gelöscht');
      },
      onError: () => {
        toast.error('Fehler beim Löschen des CID-Logos');
      }
    }
  );

  // Copy CID name to clipboard
  const copyCidName = (cidName) => {
    navigator.clipboard.writeText(`cid:${cidName}`);
    toast.success(`CID kopiert: cid:${cidName}`);
  };

  // File upload handler
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFiles(true);
    
    try {
      for (const file of files) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} ist keine gültige Bilddatei`);
          continue;
        }

        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name} ist zu groß (max. 5MB)`);
          continue;
        }

        const formData = new FormData();
        formData.append('logo', file);
        formData.append('cid_name', file.name.split('.')[0]); // Use filename without extension as CID name

        await axios.post('/api/cid-logos', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        toast.success(`${file.name} erfolgreich hochgeladen`);
      }

      queryClient.invalidateQueries('cidLogos');
      setShowUpload(false);
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Fehler beim Hochladen der Dateien');
    } finally {
      setUploadingFiles(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center">
              🎨 CID Logos
            </h1>
            <p className="text-gray-600 dark:text-gray-200">
              Verwalten Sie Ihre eingebetteten E-Mail-Logos für HTML-Nachrichten
            </p>
          </div>
          <button
            onClick={() => setShowUpload(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Logo hochladen
          </button>
        </div>
      </div>

      {/* Usage Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-blue-900 mb-1">
              So verwenden Sie CID-Logos:
            </h3>
            <div className="text-sm text-blue-800 space-y-1">
              <p>1. Laden Sie Ihre Logos hier hoch</p>
              <p>2. Verwenden Sie in HTML-E-Mails: <code className="bg-blue-100 px-1 rounded">&lt;img src="cid:logoname"&gt;</code></p>
              <p>3. Die Logos werden automatisch in die E-Mail eingebettet</p>
            </div>
          </div>
        </div>
      </div>

      {/* CID Logos Grid */}
      {cidLogos && cidLogos.length > 0 ? (
        <div className="bg-white dark:bg-dark-secondary shadow rounded-lg overflow-hidden border dark:border-dark-copper-dark">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-copper-dark">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Verfügbare CID-Logos ({cidLogos.length})
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-6">
            {cidLogos.map((logo) => (
              <div key={logo.id} className="border dark:border-dark-copper-dark rounded-lg p-4 hover:shadow-md transition-shadow bg-white dark:bg-dark-surface">
                <div className="aspect-square bg-gray-100 dark:bg-dark-primary rounded-lg mb-3 flex items-center justify-center overflow-hidden">
                  {logo.file_missing ? (
                    <div className="text-red-500 text-center">
                      <X className="h-8 w-8 mx-auto mb-2" />
                      <span className="text-xs">Datei fehlt</span>
                    </div>
                  ) : logo.image_url ? (
                    <img 
                      src={logo.image_url} 
                      alt={logo.original_filename}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : (
                    <Image className="h-12 w-12 text-gray-400" />
                  )}
                  <div className="hidden w-full h-full items-center justify-center">
                    <Image className="h-12 w-12 text-gray-400" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-200 truncate">
                    {logo.original_filename}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                      cid:{logo.name}
                    </code>
                    <button
                      onClick={() => copyCidName(logo.name)}
                      className="text-blue-600 hover:text-blue-800"
                      title="CID kopieren"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <div className="text-xs text-gray-500">
                    {logo.file_size ? `${Math.round(logo.file_size / 1024)} KB` : 'Unbekannt'}
                  </div>
                  
                  <div className="flex items-center justify-between pt-2">
                    <button
                      onClick={() => setSelectedLogo(logo)}
                      className="text-blue-600 hover:text-blue-800 flex items-center text-xs"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Details
                    </button>
                    
                    <button
                      onClick={() => deleteLogoMutation.mutate(logo.id)}
                      className="text-red-600 hover:text-red-800 flex items-center text-xs"
                      disabled={deleteLogoMutation.isLoading}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Löschen
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-12 text-center border dark:border-dark-copper-dark">
          <Image className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Keine CID-Logos</h3>
          <p className="text-gray-600 dark:text-gray-200 mb-6">
            Laden Sie Ihr erstes Logo hoch, um es in HTML-E-Mails zu verwenden
          </p>
          <button
            onClick={() => setShowUpload(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center mx-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            Erstes Logo hochladen
          </button>
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-secondary rounded-lg max-w-md w-full p-6 border dark:border-dark-copper-mid">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">CID-Logo hochladen</h3>
              <button
                onClick={() => setShowUpload(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Bilddateien auswählen
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="logo-upload"
                    disabled={uploadingFiles}
                  />
                  <label htmlFor="logo-upload" className="cursor-pointer">
                    <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-sm text-gray-600 dark:text-gray-200">
                      {uploadingFiles ? 'Wird hochgeladen...' : 'Klicken Sie hier oder ziehen Sie Dateien hierher'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      PNG, JPG, GIF bis 5MB
                    </p>
                  </label>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-xs text-yellow-800">
                  <strong>Hinweis:</strong> Der Dateiname (ohne Endung) wird als CID-Name verwendet. 
                  Beispiel: "logo.png" → "cid:logo"
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Logo Details Modal */}
      {selectedLogo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-secondary rounded-lg max-w-lg w-full p-6 border dark:border-dark-copper-mid">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Logo Details</h3>
              <button
                onClick={() => setSelectedLogo(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Dateiname</label>
                <p className="text-sm text-gray-900 dark:text-gray-200">{selectedLogo.original_filename}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">CID-Name</label>
                <div className="flex items-center justify-between">
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">cid:{selectedLogo.name}</code>
                  <button
                    onClick={() => copyCidName(selectedLogo.name)}
                    className="text-blue-600 hover:text-blue-800 flex items-center text-sm"
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    Kopieren
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Dateigröße</label>
                <p className="text-sm text-gray-900">
                  {selectedLogo.file_size ? `${Math.round(selectedLogo.file_size / 1024)} KB` : 'Unbekannt'}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">MIME-Type</label>
                <p className="text-sm text-gray-900">{selectedLogo.mime_type}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Erstellt</label>
                <p className="text-sm text-gray-900">
                  {new Date(selectedLogo.created_at).toLocaleString('de-DE')}
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-sm text-blue-800">
                  <strong>HTML-Verwendung:</strong><br />
                  <code className="bg-blue-100 px-1 rounded">&lt;img src="cid:{selectedLogo.name}" alt="Logo"&gt;</code>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CidLogos;